/**
 * RouteStatusPage -- full-screen shell shared by the 404 page and the unexpected-route-error
 * page (FEAT-047 design.md 1.1). It does not depend on the app layout, so it can be the
 * root errorElement and looks the same for anonymous users and every role.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { Link } from 'react-router';
import { BrandLogo } from '@/components/BrandLogo';
import { cn } from '@/utils/cn';

interface RouteStatusPageProps {
  /** Decorative icon (aria-hidden is applied by the caller's icon props). */
  icon: ReactNode;
  /** Small label above the title (e.g. "Erro 404"); omitted for unexpected errors. */
  eyebrow?: string;
  title: string;
  description: string;
  /** Primary and secondary actions. */
  actions: ReactNode;
  /** Destination of the logo link. */
  homePath: string;
  /** Accessible name of the logo link. */
  homeLabel: string;
}

const CASCADE_STEP_MS = 75;

export function RouteStatusPage({
  icon,
  eyebrow,
  title,
  description,
  actions,
  homePath,
  homeLabel,
}: RouteStatusPageProps) {
  const titleRef = useRef<HTMLHeadingElement>(null);

  // Announce the page to screen readers: move focus to the h1 on mount.
  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  const step = (index: number) => ({ animationDelay: `${index * CASCADE_STEP_MS}ms` });

  return (
    <div className="flex min-h-screen flex-col bg-bg-primary text-text-primary">
      <header className="p-6">
        <Link to={homePath} aria-label={homeLabel} className="inline-block">
          <BrandLogo variant="sidebar" />
        </Link>
      </header>

      <main className="flex flex-1 items-center justify-center px-6 py-12 sm:py-16">
        <div className="flex w-full max-w-md flex-col items-start">
          <div className="animate-fade-in-up text-text-tertiary" style={step(0)}>
            {icon}
          </div>
          {eyebrow && (
            <p
              className="animate-fade-in-up mt-4 text-xs font-medium uppercase tracking-wider text-text-tertiary"
              style={step(1)}
            >
              {eyebrow}
            </p>
          )}
          <h1
            ref={titleRef}
            tabIndex={-1}
            className={cn(
              'animate-fade-in-up mt-2 text-xl font-bold text-text-primary outline-none sm:text-2xl',
            )}
            style={step(2)}
          >
            {title}
          </h1>
          <p
            className="animate-fade-in-up mt-3 text-base text-text-secondary"
            style={step(3)}
          >
            {description}
          </p>
          <div
            className="animate-fade-in-up mt-8 flex w-full flex-col gap-3 sm:w-auto sm:flex-row"
            style={step(4)}
          >
            {actions}
          </div>
        </div>
      </main>
    </div>
  );
}
